import { useEffect, useRef } from 'react';

interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  language?: string;
  height?: string;
  readOnly?: boolean;
  placeholder?: string;
}

/**
 * Code editor component using Monaco Editor.
 * Note: Requires @monaco-editor/react to be installed.
 * Install with: npm install @monaco-editor/react
 */
export const CodeEditor = ({
  value,
  onChange,
  language = 'plaintext',
  height = '400px',
  readOnly = false,
  placeholder = 'Enter code...',
}: CodeEditorProps) => {
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadMonaco = async () => {
      try {
        const MonacoEditor = (await import('@monaco-editor/react')).default as any;
        
        if (containerRef.current && !editorRef.current) {
          editorRef.current = MonacoEditor;
        }
      } catch (error) {
        console.error('Failed to load Monaco Editor. Please install: npm install @monaco-editor/react', error);
      }
    };

    loadMonaco();
  }, []);

  if (!editorRef.current) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        className="w-full font-mono text-sm p-4 border border-[var(--mui-palette-divider)] rounded resize-vertical bg-transparent"
        style={{ minHeight: height }}
      />
    );
  }

  const MonacoEditor = editorRef.current;
  return (
    <div ref={containerRef} className="border border-[var(--mui-palette-divider)] rounded">
      <MonacoEditor
        height={height}
        language={language}
        value={value}
        onChange={(val: string | undefined) => onChange?.(val || '')}
        theme="vs-dark"
        options={{
          readOnly,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          fontSize: 14,
          lineNumbers: 'on',
          automaticLayout: true,
        }}
      />
    </div>
  );
};
